import { jsx } from '@emotion/react';
import { useState } from 'react';
import { Animation, Avatar, FloatingBox, useClickOutside, } from '@zapier/design-system';
import { Tooltip } from '@zapier/design-system-beta';
import { labelStyles } from '@zapier/style-encapsulation';
import { createUniqueClassName } from '../../utils/createUniqueClassName';
import UniversalNavLink from '../UniversalNavLink';
import { AccountsMenuLinks } from '../../constants';
const detailsClassName = createUniqueClassName('AccountsMenu', 'details');
const MENU_ITEM_HEIGHT = '48px';
const Styles = labelStyles('AccountsMenu', {
    root: {
        position: 'relative',
    },
    accounts: {
        maxHeight: `calc(${MENU_ITEM_HEIGHT} * 3.5)`,
        overflowY: 'auto',
        '& > a': {
            padding: 'var(--zds-space-4, 4px) var(--zds-space-8, 8px) !important',
        },
    },
    summary: {
        position: 'relative',
        display: 'block',
        cursor: 'pointer',
        transition: Animation.transitionValue,
        '&::-webkit-details-marker': {
            display: 'none',
        },
    },
    divider: {
        margin: 'var(--zds-space-8, 8px) 0',
        height: 1,
        backgroundColor: 'var(--zds-stroke-weaker, #D7D3C9)',
        border: 'none',
    },
    menuContainer: {
        padding: 'var(--zds-space-8, 8px)',
        width: 278,
    },
    email: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '0 var(--zds-space-8, 8px)',
        font: `var(--zds-small-print-1)`,
        color: 'var(--zds-text-weaker, #574E4C)',
        marginBottom: 'var(--zds-space-8, 8px)',
    },
    emailText: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
});
const renderAccountSwitchMenu = (accounts, currentAccountId, onAccountClick, setIsMenuOpen) => {
    return (jsx("div", { css: Styles.accounts, "data-zds": true }, accounts.map((account) => {
        const isCurrentAccount = account.id === currentAccountId;
        return (jsx(UniversalNavLink, { isV3Link: true, href: account.accountSwitcherUrl, isSelected: isCurrentAccount, key: account.id, onClick: (event) => {
                if (onAccountClick) {
                    onAccountClick(account, event);
                }
                setIsMenuOpen(false);
            }, renderBeforeText: () => (jsx("div", { css: Styles.avatar, "data-zds": true },
                jsx(Avatar, { isBlock: true, isSelected: false, name: account.name, shouldLazilyLoad: false, size: "medium", url: account.imageUrl }))), role: "menuitem", text: account.name, secondaryText: account.planName, showCheckmarkOnSelected: true }));
    })));
};
const renderMenuLinks = (menuLinks, setIsMenuOpen) => {
    return menuLinks.map((menuLink) => (jsx(UniversalNavLink, { isV3Link: true, href: menuLink.href, icon: typeof menuLink.icon === 'object'
            ? menuLink.icon.selected
            : menuLink.icon, key: menuLink.text, onClick: () => {
            if (menuLink.onClick) {
                menuLink.onClick();
            }
            setIsMenuOpen(false);
        }, role: "menuitem", text: menuLink.text, target: menuLink.target })));
};
const fallbackAccount = {
    id: -1,
    name: '⋯',
    accountSwitcherUrl: '/',
};
const AccountsMenu = ({ accounts, email, currentAccountId = undefined, hasWhiteBackground = undefined, menuLabel = 'Accounts Settings', menuLinks = AccountsMenuLinks, onAccountClick = undefined, size = 'medium', }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [setClickOutsideNode] = useClickOutside({
        onClickOutside: () => setIsMenuOpen(false),
    });
    const currentAccount = accounts.find((account) => account.id === currentAccountId) ||
        fallbackAccount;
    const toggleMenu = (event) => {
        event.preventDefault();
        setIsMenuOpen(!isMenuOpen);
    };
    return (jsx("div", { css: Styles.root, "data-zds": true },
        jsx("details", { className: detailsClassName, "data-testid": "accountsMenu", open: isMenuOpen, ref: (node) => setClickOutsideNode(node), "data-zds": true },
            jsx(Tooltip, { content: currentAccount.name, placement: "left" },
                jsx(Tooltip.FocusableTrigger, { "aria-haspopup": "menu", "aria-label": menuLabel, css: Styles.summary, "data-testid": "accounts-menu-summary", onClick: toggleMenu, tag: "summary" },
                    jsx(Avatar, { isBlock: true, isInteractive: true, isSelected: isMenuOpen, name: currentAccount.name, shouldLazilyLoad: false, size: size === 'small' ? 'small' : 'medium', url: currentAccount.imageUrl }))),
            jsx(FloatingBox, { align: "right", hasWhiteBackground: hasWhiteBackground, position: "south", borderRadius: "medium" },
                jsx("div", { css: Styles.menuContainer, role: "menu", "data-zds": true },
                    jsx("div", { css: Styles.email, "data-zds": true },
                        jsx("span", { css: Styles.emailText, "data-zds": true }, email)),
                    renderAccountSwitchMenu(accounts, currentAccountId, onAccountClick, setIsMenuOpen),
                    jsx("hr", { css: Styles.divider }),
                    renderMenuLinks(menuLinks, setIsMenuOpen))))));
};
export default AccountsMenu;
//# sourceMappingURL=AccountsMenu.js.map
// @zapier/universal-layout@11.0.40 (30311736a)
